import React from 'react';
import Grid from "@mui/material/Grid";
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import StaffMember from "../../../components/StaffMember";
import ControlledTextField from '../../../components/ControlledTextField';
import { postApiOrThrow, getAPIDataParams, getAPIDataParamsReturn, getAPIDataReturn } from "../../../utils/apiFunction";

const CompleteForDay = (props) => {
    const Swal = useSwalWrapper();
    const [remaining, setRemaining] = React.useState(null);
    const [complete, setComplete] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [outs, setOuts] = React.useState(null);

    // Mixed group detection from selected bench
    const mixedGroupId = props.selectedBench?.mixedGroupId || props.selectedBench?.MixedGroupId;
    const groupItems = Array.isArray(props.selectedBench?.groupItems) ? props.selectedBench.groupItems : [];
    const isMixed = !!(mixedGroupId && groupItems.length > 0);
    const runIds = isMixed
        ? groupItems
            .map(gi => Number(gi?.id ?? gi?.processRunId))
            .filter(n => Number.isFinite(n))
        : [props.selectedBench?.id].filter(Boolean);

    React.useEffect(() => {
        const loadOutputs = async () => {
            if (isMixed && runIds.length > 0) {
                // Fetch outputs for all sibling runs and combine
                const lists = await Promise.all(
                    runIds.map(id => getAPIDataParamsReturn('/Processing/GetProcessingOutputs', { ProcessRunId: id }))
                );
                const combined = lists.flat().filter(Boolean);
                setOuts(combined);
            } else if (props.selectedBench?.id) {
                getAPIDataParams('/Processing/GetProcessingOutputs', setOuts, { ProcessRunId: props.selectedBench.id });
            } else {
                setOuts([]);
            }
        };
        loadOutputs();
    }, [props.selectedBench, isMixed]);
    const validate = () => {
        var warningMessages = [];
        var errorMessages = [];
        //console.log(finished)
        console.log(outs)
        // Validation: if multiple runs, it's OK if some runs have no outputs.
        // Only error when across all runs there are zero outputs AND remaining isn't provided.
        const multipleRuns = runIds.length > 1;
        const hasAnyOutputsAcrossRuns = Array.isArray(outs) && outs.length > 0;
        if (!hasAnyOutputsAcrossRuns && ((remaining === null) || (remaining === 0))) {
            errorMessages.push(multipleRuns
                ? 'No outputs recorded. Adjust remaining to zero to complete, or add outputs.'
                : 'There are no outputs for this stock, adjust stock to zero to complete');
        }


        const formatErrorMessage = (array) => {
            var errString = '';
            array.map((a) => {
                errString = errString + a + '<br/>';
            });
            return errString;
        }
        if (errorMessages.length > 0) {

            Swal.fire({
                html: formatErrorMessage(errorMessages),
                icon: 'error',
                title: 'Oops...',

            });
        } else if (warningMessages.length > 0) {
            Swal.fire({
                html: formatErrorMessage(warningMessages),
                icon: 'warning',
                title: 'Oops...',
                showCancelButton: true,
                confirmButtonText: 'Yes, Continue',
                cancelButtonText: 'No, I\'ll edit it',

            }).then(result => {
                if (result.value) {
                    submit();
                } else if (result.dismiss === Swal.DismissReason.cancel) {

                }
            });
        } else {
            submit();
        }
    }
    const submit = async (finished) => {
        var resp = null;
        setError(false);
        setLoading(true);
        try {
        if (complete) {
            if (isMixed && mixedGroupId) {
                // End all runs in the mixed group with one call
                const endMixed = await postApiOrThrow('/Processing/EndProcessingMixed', { MixedGroupId: mixedGroupId });
                resp = endMixed;
            } else {
                const endparams = { processRunId: props.selectedBench.id };
                resp = await postApiOrThrow('/Processing/EndProcessing', endparams);
            }
        } else {
            // Not complete: distribute remaining across all runs and update each, then end
            if (isMixed && mixedGroupId && groupItems.length > 0) {
                const baseTotal = groupItems.reduce((p, gi) => p + Number(gi?.quantity ?? 0), 0);
                const totalRemaining = Math.max(0, Number(remaining || 0));
                // Build proportional remaining distribution with rounding correction
                let allocations = [];
                if (baseTotal > 0) {
                    allocations = groupItems.map(gi => ({
                        gi,
                        remainingShare: Math.floor((Number(gi?.quantity ?? 0) / baseTotal) * totalRemaining)
                    }));
                    const allocated = allocations.reduce((p, a) => p + a.remainingShare, 0);
                    let diff = totalRemaining - allocated;
                    let idx = 0;
                    while (diff !== 0 && idx < allocations.length) {
                        allocations[idx].remainingShare += (diff > 0 ? 1 : -1);
                        diff += (diff > 0 ? -1 : 1);
                        idx++;
                    }
                } else {
                    // No base quantity: split evenly
                    const n = groupItems.length || 1;
                    const even = Math.floor(totalRemaining / n);
                    allocations = groupItems.map(gi => ({ gi, remainingShare: even }));
                    let rem = totalRemaining - (even * n);
                    let i = 0;
                    while (rem > 0 && i < allocations.length) { allocations[i].remainingShare += 1; rem--; i++; }
                }

                // Optional: fetch current runs to preserve bins/bags per run
                const runDetails = await Promise.all(runIds.map(id => getAPIDataReturn('/Processing/GetProcessRun/' + id)));
                const byId = new Map(runDetails.filter(Boolean).map(r => [Number(r.id), r]));

                // Update each run with processed amount = original - remainingShare
                for (const { gi, remainingShare } of allocations) {
                    const runId = Number(gi?.id ?? gi?.processRunId);
                    const origQty = Number(gi?.quantity ?? 0);
                    const processedAmt = Math.max(0, origQty - Number(remainingShare ?? 0));
                    const rd = byId.get(runId) || {};
                    const params = {
                        ProcessRunId: runId,
                        ProductionLineId: props.selectedBench?.benchDetails?.id,
                        Amount: processedAmt,
                        Bins: Number.isFinite(rd?.bins) ? rd.bins : (props.selectedBench?.bins ?? 0),
                        Bags: Number.isFinite(rd?.bags) ? rd.bags : (props.selectedBench?.bags ?? 0),
                        Staff: props.selectedBench?.staff,
                        StaffMemberId: props.selectedBench?.staffMember?.id
                    };
                    await postApiOrThrow('/Processing/UpdateProcess', params);
                }

                // End all runs in the mixed group with one call
                const endMixed = await postApiOrThrow('/Processing/EndProcessingMixed', { MixedGroupId: mixedGroupId });
                resp = endMixed;
            } else {
                // Single run path
                const params = {
                    ProcessRunId: props.selectedBench.id,
                    ProductionLineId: props.selectedBench?.benchDetails?.id,
                    Amount: props.selectedBench.quantity - remaining,
                    Bins: props.selectedBench.bins,
                    Bags: props.selectedBench.bags,
                    Staff: props.selectedBench.staff,
                    StaffMemberId: props.selectedBench.staffMember.id
                }
                await postApiOrThrow('/Processing/UpdateProcess', params).then(async (out) => {
                    const endparams = { processRunId: props.selectedBench.id };
                    resp = await postApiOrThrow('/Processing/EndProcessing', endparams);
                });
            }
        }
        if (resp.ok) {
            props.handleClose();
        }
        } catch (submitError) {
            setError(submitError?.message || "Failed to submit");
            setLoading(false);
        }
    }
    return <Grid container spacing={2} mb={1}>
        <Grid item xs={12}>
            <FormControlLabel style={{ paddingLeft: '9px' }} control={<Checkbox style={{ paddingLeft: '0px', paddingRight: '0px' }} checked={complete} onChange={(event) => { setComplete(event.target.checked); }} />} label="This Input is Completed" disabled={props.disabled} labelPlacement="right" />
        </Grid>
        {!complete ? <Grid item xs={12}>
            <Typography variant={'h5'}>Amount Remaining</Typography>
            <ControlledTextField label="Add to Input" type="number" name="add" state={remaining} setState={setRemaining} number />
        </Grid> : null}
        <Grid item xs={12}>
            {error ? <Typography color="error">{error}</Typography> : null}
            {!loading && outs ? <Button variant="contained" color="primary" style={{ whiteSpace: 'nowrap', height: '50px', margin: 0 }} onClick={validate} handleClose={props.handleClose} >Complete</Button> : <CircularProgress />}
        </Grid>
    </Grid>

}


export default CompleteForDay;
